// Default mapping from a standard material to an interventie in the
// milieu-circulariteit standaard, plus how many registration units one product
// unit represents.
//
// WHY A FACTOR AT ALL. The standaard's own afbakening spells this out under
// "Productlaag (materialenlijst-energiebox)":
//
//   "Product-eenheid (koker, rol, stuks) omrekenen naar registratie-eenheid
//    (strekkende meter, m2) VOOR de CO2-/EUR-berekening."
//
// So a koker of kit is not one unit of ET03 — it is however many metres of kier
// that koker seals. That number is a property of the product an organisation
// actually buys, not a constant of the standaard.
//
// null means "we do not know, and refuse to guess". A material with a null
// factor reports `needs-factor` and contributes nothing until someone fills the
// field in on the Materials page. Defaulting to 1 would silently bill one koker
// as one metre.
//
// MANY-TO-ONE IS INTENTIONAL. Several products point at one code — the
// standaard names exactly this case ("tochtband/kit/dorpel/kaderprofiel ->
// ET03") and requires the effect be counted once per code per dwelling, not
// once per product. That dedup happens downstream in computeHouseholdImpact,
// not here.
//
// Materials with no entry are not a bug: 8 of the standard materials either
// have no matching interventie (magneten, ontluchtsleutel, verlengstuk,
// tafelcontactdoos) or map to one the standaard cannot yet quantify
// (deursluiter ET19, bewegingssensor ET20, sokken). Those must read as
// "geen kengetal", never as zero.
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

/**
 * @typedef {object} MaterialInterventionDefault
 * @property {string} code - Interventie id in the milieu-circulariteit standaard
 * @property {number|null} unitsPerItem - Registration units per product unit; null = tenant must supply
 */

/** @type {Object<string, MaterialInterventionDefault>} */
const MATERIAL_INTERVENTION_DEFAULTS = {
    // --- Tocht & isolatie --------------------------------------------------
    // ET03 is 'strekkende meter' kierdichting. A material already measured in
    // metres is 1:1; anything sold per piece or per koker needs a real number.
    'Tochtband rubber': { code: 'ET03', unitsPerItem: 1 },
    'Tochtband schuim': { code: 'ET03', unitsPerItem: 1 },
    'Tochtstrip onderdeur': { code: 'ET03', unitsPerItem: null },
    'Aluminium kaderprofiel': { code: 'ET03', unitsPerItem: null },
    'Kunststof kaderprofiel': { code: 'ET03', unitsPerItem: null },
    'Kit (transparant)': { code: 'ET03', unitsPerItem: null },
    'Kit (wit)': { code: 'ET03', unitsPerItem: null },
    'Dorpel': { code: 'ET03', unitsPerItem: null },
    'Dorpelprofiel borstel': { code: 'ET03', unitsPerItem: null },
    'Dorpelprofiel rubber': { code: 'ET03', unitsPerItem: null },

    'Brievenbusborstel': { code: 'ET16', unitsPerItem: 1 },
    'Brievenbusborstel aluminium': { code: 'ET16', unitsPerItem: 1 },

    // Rule 10: on the same m2 of glass it is EITHER kozijnfolie (ET15) OR
    // glasfolie (ET15b), never both. Two products, two codes, one per window.
    'Kozijnfolie': { code: 'ET15', unitsPerItem: null },
    'Raamfolie (enkelglas)': { code: 'ET15b', unitsPerItem: null },

    'Gordijn': { code: 'ET04', unitsPerItem: null },

    // Deursluiter -> ET19, bewegingssensor -> ET20: both sit in
    // controle.zonderKengetal. Mapped so the UI can say which interventie they
    // belong to, while the adapter reports status 'no-kengetal'.
    'Deursluiter Axa': { code: 'ET19', unitsPerItem: 1 },
    'Deursluiter Opdekdeur': { code: 'ET19', unitsPerItem: 1 },

    // --- Verwarming & energie ---------------------------------------------
    'Radiatorfolie': { code: 'ET01', unitsPerItem: null },

    // --- Elektriciteit -----------------------------------------------------
    // ET02 is priced 'per lamp', so every LED variant is 1:1 regardless of
    // fitting or wattage.
    'LED-lamp E27': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E14': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp GU10': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E14 - 1W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E14 - 2W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E14 - 4W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E27 - 1W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E27 - 3W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E27 - 5W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E27 - 7,5W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp E27 - 10,5 W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp G9 - 1,8W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp G9 - 3,5W': { code: 'ET02', unitsPerItem: 1 },
    'LED-lamp GU10 - 3,5W': { code: 'ET02', unitsPerItem: 1 },

    'Tijdschakelaar': { code: 'ET17', unitsPerItem: 1 },
    'Bewegingssensor': { code: 'ET20', unitsPerItem: 1 },

    // --- Waterbesparing & comfort -----------------------------------------
    // ET07 and ET09 are priced per bewoner, ET10 per woning. Their factor is
    // 1 because the scaling comes from the interventie's scope, not from a
    // unit conversion.
    'Waterbesparende douchekop': { code: 'ET07', unitsPerItem: 1 },
    'Douchetimer - zandloper': { code: 'ET09', unitsPerItem: 1 },
    'Douchetimer - digitaal': { code: 'ET09', unitsPerItem: 1 },
    'Radiatorventilator': { code: 'ET10', unitsPerItem: 1 },

    'Perlator (kraan)': { code: 'ET14', unitsPerItem: 1 },
    'Vensterbankrooster': { code: 'ET18', unitsPerItem: 1 },

    // Rule 13: warme sokken counts as an output, its saving runs via EG04.
    'Sokken': { code: 'warme-sokken-kleding-comfort-enabler', unitsPerItem: 1 },
}

/**
 * Materials whose factor an organisation has to supply before they can produce
 * a number. Exposed so the Materials page can point at exactly these rows.
 *
 * @returns {Array<string>}
 */
const materialsNeedingFactor = () =>
    Object.entries(MATERIAL_INTERVENTION_DEFAULTS)
        .filter(([, value]) => value.unitsPerItem === null)
        .map(([title]) => title)

/**
 * @param {string} title - A Materials doc Title
 * @returns {MaterialInterventionDefault|null}
 */
const defaultForMaterial = (title) => MATERIAL_INTERVENTION_DEFAULTS[title] || null

export { MATERIAL_INTERVENTION_DEFAULTS, materialsNeedingFactor, defaultForMaterial }
